/**
 * useArchivedActivitiesData — les pages d'activités archivées (patron B).
 *
 * `getArchivedActivities` n'a aucun cas SUCCESS dans un réducteur : la réponse est consommée
 * ici, comme le legacy la range dans son `useState` (`ArchivedActivitiesScreen.js:76-92`). Les
 * paramètres sont ceux de sa `PaginatedFlatList` (`:301-304`) : `{student}` en route,
 * `{per_page: 20, with_logs: 1, page}` en requête, page 1 au montage, page suivante à la
 * demande — `with_logs` nourrit la fiche de l'activité.
 *
 * L'élève est résolu comme `ArchivedActivitiesScreen.js:50` : le paramètre de route d'abord,
 * l'utilisateur connecté ensuite — en mode parent, c'est l'enfant.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

/** Le legacy charge par vingt (`ArchivedActivitiesScreen.js:49`). */
const PER_PAGE = 20

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    urlParams?: { page?: number }
    data?: {
        activities?: ApiActivity[]
        meta?: { current_page?: number; last_page?: number; total?: number }
    }
}

type PageCursor = {
    currentPage: number
    lastPage: number
    totalCount: number
}

const INITIAL_CURSOR: PageCursor = { currentPage: 1, lastPage: 1, totalCount: 0 }

export function useArchivedActivitiesData(routeStudentId: string | number | null | undefined) {
    const dispatch = useDispatch()
    const displayedStudentId = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => selectDisplayedStudent(state)?.id as string | number | undefined
    )
    const studentId = routeStudentId ?? displayedStudentId ?? null

    const req = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.getArchivedActivities as ApiSlice | undefined
    )

    /** Les lignes reçues, page après page — `null` tant que rien n'est arrivé. */
    const [raw, setRaw] = useState<ApiActivity[] | null>(null)
    const [cursor, setCursor] = useState<PageCursor>(INITIAL_CURSOR)
    const handledSuccessRef = useRef(false)
    const loadMoreInFlightRef = useRef(false)

    const fetchPage = useCallback(
        (page: number) => {
            if (studentId == null) return
            handledSuccessRef.current = false
            dispatch(
                apiRequest(
                    activitiesService.getArchivedActivities,
                    { student: studentId },
                    {},
                    { per_page: PER_PAGE, with_logs: 1, page }
                ) as never
            )
        },
        [dispatch, studentId]
    )

    useEffect(() => {
        setRaw(null)
        setCursor(INITIAL_CURSOR)
        loadMoreInFlightRef.current = false
        fetchPage(1)
    }, [fetchPage])

    useEffect(() => {
        if (!req?.success || handledSuccessRef.current) return
        handledSuccessRef.current = true
        const page = Number(req.urlParams?.page ?? 1)
        const received = req.data?.activities ?? []
        setRaw((previous) => (page > 1 ? [...(previous ?? []), ...received] : received))
        const meta = req.data?.meta
        setCursor({
            currentPage: meta?.current_page ?? page,
            lastPage: meta?.last_page ?? page,
            totalCount: meta?.total ?? received.length,
        })
        loadMoreInFlightRef.current = false
        dispatch(apiIdle(activitiesService.getArchivedActivities) as never)
    }, [dispatch, req?.success, req?.data, req?.urlParams?.page])

    useEffect(() => {
        if (!req?.error) return
        loadMoreInFlightRef.current = false
        // Une page en échec ne doit pas laisser le curseur promettre une suite.
        setCursor((previous) => ({ ...previous, lastPage: previous.currentPage }))
    }, [req?.error])

    useEffect(
        () => () => {
            dispatch(apiIdle(activitiesService.getArchivedActivities) as never)
        },
        [dispatch]
    )

    const hasMore = cursor.currentPage < cursor.lastPage

    /** Rafraîchir garde la liste à l'écran : la page 1 la remplace à son retour. */
    const refresh = useCallback(() => {
        loadMoreInFlightRef.current = false
        fetchPage(1)
    }, [fetchPage])

    const loadMore = useCallback(() => {
        if (loadMoreInFlightRef.current || !!req?.request || !hasMore) return
        loadMoreInFlightRef.current = true
        fetchPage(cursor.currentPage + 1)
    }, [cursor.currentPage, fetchPage, hasMore, req?.request])

    return { studentId, raw, refresh, loadMore, hasMore }
}
